import React, { useState, useEffect, useCallback } from "react";
import { Card, Space, Button, Select, Switch } from "antd";
import { PlusOutlined, SyncOutlined, TeamOutlined } from "@ant-design/icons";
import useActivityParticipation from "../../../hooks/academics/useActivityParticipation";
import useCalendarEvents from "../../../hooks/academics/useCalendarEvents";
import useCurricularCalendars from "../../../hooks/academics/useCurricularCalendars";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import ActivityParticipationTable from "../../../components/academics/activity_participation/ActivityParticipationTable";
import ActivityParticipationFormModal from "../../../components/academics/activity_participation/ActivityParticipationFormModal";
import ValuesStore from "../../../store/values-store";

const INVOLVEMENT_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Occasional", label: "Occasional" },
  { value: "Non-participant", label: "Non-participant" },
];

const RATING_OPTIONS = [
  { value: "Excellent", label: "Excellent" },
  { value: "Good", label: "Good" },
  { value: "Average", label: "Average" },
  { value: "Needs Improvement", label: "Needs Improvement" },
];

const CATEGORY_OPTIONS = [
  { value: "MDD", label: "Music, Dance & Drama" },
  { value: "Sports", label: "Sports & Physical Education" },
  { value: "Languages", label: "Languages Extension Clubs" },
  { value: "Academic Clubs", label: "Academic Clubs & Societies" },
  { value: "Special Interest", label: "Special Interest Activities" },
];

const ActivityParticipationManagement = () => {
  const valuesStore = ValuesStore();
  const myRoles =
    valuesStore.getValue("myRoles") ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const isAdmin =
    Array.isArray(myRoles) && myRoles.includes("System Administrator");

  const {
    participations,
    loading,
    fetchParticipations,
    recordParticipation,
    updateParticipation,
    deleteParticipation,
  } = useActivityParticipation();

  const { events, fetchEvents } = useCalendarEvents();
  const { calendars, fetchCalendars } = useCurricularCalendars();
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [filterEventID, setFilterEventID] = useState(null);
  const [filterCalendarID, setFilterCalendarID] = useState(null);
  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [filterInvolvement, setFilterInvolvement] = useState(null);
  const [filterRating, setFilterRating] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterHasAward, setFilterHasAward] = useState(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filterEventID) params.eventID = filterEventID;
    if (filterCalendarID) params.calendarID = filterCalendarID;
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterInvolvement) params.involvementLevel = filterInvolvement;
    if (filterRating) params.performanceRating = filterRating;
    if (filterCategory) params.category = filterCategory;
    if (filterHasAward !== null && filterHasAward !== undefined)
      params.hasAward = filterHasAward;
    fetchParticipations(params);
  }, [
    fetchParticipations,
    filterEventID,
    filterCalendarID,
    filterAcadyrID,
    filterTermID,
    filterInvolvement,
    filterRating,
    filterCategory,
    filterHasAward,
  ]);

  useEffect(() => {
    fetchEvents();
    fetchCalendars();
    fetchAcademicYears();
    fetchTerms();
  }, [fetchEvents, fetchCalendars, fetchAcademicYears, fetchTerms]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave(values) {
    const result = editRecord
      ? await updateParticipation(editRecord.id, values)
      : await recordParticipation(values);
    if (result) {
      setModalVisible(false);
      setEditRecord(null);
      load();
    }
  }

  function handleEdit(record) {
    setEditRecord(record);
    setModalVisible(true);
  }

  async function handleDelete(id) {
    const ok = await deleteParticipation(id);
    if (ok) load();
  }

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h4 style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          Activity Participation
        </h4>
        <Space>
          <Button icon={<SyncOutlined />} onClick={load}>
            Refresh
          </Button>
          {isAdmin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditRecord(null);
                setModalVisible(true);
              }}
            >
              Record Participation
            </Button>
          )}
        </Space>
      </div>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Filter by Event"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            onChange={setFilterEventID}
            options={events.map((e) => ({
              value: e.id,
              label: `${e.title} (${e.scheduledDate})`,
            }))}
          />
          <Select
            placeholder="Calendar"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            onChange={setFilterCalendarID}
            options={calendars.map((c) => ({
              value: c.id,
              label: `${c.termName} ${c.academicYear}`,
            }))}
          />
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 140 }}
            onChange={setFilterAcadyrID}
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 120 }}
            onChange={setFilterTermID}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Category"
            allowClear
            style={{ width: 180 }}
            onChange={setFilterCategory}
            options={CATEGORY_OPTIONS}
          />
          <Select
            placeholder="Involvement"
            allowClear
            style={{ width: 160 }}
            onChange={setFilterInvolvement}
            options={INVOLVEMENT_OPTIONS}
          />
          <Select
            placeholder="Rating"
            allowClear
            style={{ width: 160 }}
            onChange={setFilterRating}
            options={RATING_OPTIONS}
          />
          <Space>
            <Switch
              checked={filterHasAward === true}
              onChange={(v) => setFilterHasAward(v ? true : null)}
              size="small"
            />
            <span style={{ fontSize: 12 }}>Awards Only</span>
          </Space>
        </Space>
      </Card>

      <ActivityParticipationTable
        participations={participations}
        loading={loading}
        isAdmin={isAdmin}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <ActivityParticipationFormModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditRecord(null);
        }}
        onSave={handleSave}
        loading={loading}
        editRecord={editRecord}
        events={events}
      />
    </div>
  );
};

export default ActivityParticipationManagement;
